import { MarkdownText } from './MarkdownText.jsx';
import { ThinkingPane } from './ThinkingPane.jsx';
import { ToolCallBadge } from './ToolCallBadge.jsx';

import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * MessageBubble — 單則訊息渲染
 *
 * Assistant 訊息：
 *   - 進行中（status='running'）：顯示所有 tool badges（多工具堆疊，自動 expand）
 *   - 完成後：tool badges 預設 collapse，點 N 個工具步驟可展開查看
 *
 * ⚠️ memo 包起來：ChatPane 的輸入框 input state 每按一鍵就重渲染整個 pane，
 *    若不 memo，每則訊息的 MarkdownText（react-markdown + Prism 高亮）會跟著重解析，
 *    對話一長（含大 JSON code block）打字就會明顯卡頓。message reference 沒變就跳過。
 */
function MessageBubbleInner({ message }) {
    const { t } = useTranslation('workspace');
    const isUser = message.role === 'user';
    if (isUser) {
        // displayText = 純用戶需求摘要（modal 觸發時帶）；沒有就顯示完整 text（用戶自己打字的情況）
        return (
            <div className="flex justify-end my-2">
                <div className="max-w-[80%] px-4 py-2 rounded-2xl rounded-br-md bg-accent-50 text-ink-900 text-sm whitespace-pre-wrap">
                    {message.displayText || message.text}
                </div>
            </div>
        );
    }

    const status = message.status;
    const toolCalls = message.toolCalls || [];
    const isRunning = status === 'running' || !status;
    const [toolsExpanded, setToolsExpanded] = useState(false);
    const showToolsInline = isRunning || toolsExpanded;

    return (
        <div className="flex justify-start my-3">
            <div className="max-w-[90%] w-full">
                <div className="rounded-2xl rounded-bl-md bg-white border border-cream-200 px-4 py-3">
                    {message.thinking && <ThinkingPane text={message.thinking} />}

                    {/* 推理中：自動 expand 顯示所有工具；完成後：collapse 成一個 chip，點開可看 */}
                    {showToolsInline && toolCalls.map((tc, i) => (
                        <ToolCallBadge key={i} tool={tc} />
                    ))}
                    {!isRunning && toolCalls.length > 0 && (
                        <button onClick={() => setToolsExpanded((v) => !v)}
                            className="text-[11px] text-ink-500 hover:text-accent-700 font-mono mb-1">
                            {toolsExpanded ? t('bubble.collapse') : '▶ '}{t('bubble.toolSteps', { n: toolCalls.length })}
                        </button>
                    )}

                    {message.text && <MarkdownText>{message.text}</MarkdownText>}

                    {status === 'interrupted' && (
                        <div className="mt-2 inline-flex items-center gap-1 text-xs italic text-ink-400">
                            <span>{t('bubble.interrupted')}</span>
                        </div>
                    )}
                    {status === 'timed_out' && (
                        <div className="mt-2 inline-flex items-center gap-1 text-xs italic text-ink-400">
                            <span>{t('bubble.timedOut')}</span>
                        </div>
                    )}
                    {status === 'failed' && (
                        /* 0805 東訊回報：原本只印「執行失敗 (exit 1)」,使用者不知道發生什麼、
                           也不知道下一步。改成「原因 + 怎麼辦」,exit code 與 stderr 收進展開細節。 */
                        <div className="mt-2 text-xs text-danger-500">
                            <div>{t(`bubble.fail.${message.failReason || 'crashed'}`, { defaultValue: t('bubble.failed') })}</div>
                            {(message.failDetail || message.exitCode != null) && (
                                <details className="mt-1 text-ink-400">
                                    <summary className="cursor-pointer select-none hover:text-ink-600">
                                        {t('bubble.failDetails')}
                                    </summary>
                                    <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-cream-100 p-2 text-[11px] leading-relaxed">
{message.exitCode != null ? `exit ${message.exitCode}\n` : ''}{message.failDetail || ''}
                                    </pre>
                                </details>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export const MessageBubble = memo(MessageBubbleInner);
